import React, { useState, useEffect } from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { usersApi } from '../api/users';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'staff' | 'user' | 'viewer';
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: User['role'];
  isActive: boolean;
}

interface UpdateUserData {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: User['role'];
  isActive?: boolean;
}

const roleLabels = {
  admin: 'Administrator',
  manager: 'Manager',
  staff: 'Staff',
  user: 'User',
  viewer: 'Viewer',
};

const roleColors = {
  admin: 'bg-rose-100 text-rose-800',
  manager: 'bg-cyan-100 text-cyan-800',
  staff: 'bg-emerald-100 text-emerald-800',
  user: 'bg-violet-100 text-violet-800',
  viewer: 'bg-purple-100 text-purple-800',
};

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<User['role'] | 'ALL'>('ALL');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Users are loaded from the API

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.getAll();
      setUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]); // Show empty state instead of mock data
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      const newUser = await usersApi.create(userData);
      setUsers(prev => [newUser, ...prev]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleUpdateUser = async (userData: UpdateUserData) => {
    if (!selectedUser) return;
    
    try {
      const updatedUser = await usersApi.update(selectedUser.id, userData);
      setUsers(prev => prev.map(user => user.id === selectedUser.id ? updatedUser : user));
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await usersApi.delete(selectedUser.id);
      setUsers(prev => prev.filter(user => user.id !== selectedUser.id));
      setShowDeleteConfirm(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      const updatedUser = await usersApi.toggleActive(user.id);
      setUsers(prev => prev.map(u => u.id === user.id ? updatedUser : u));
    } catch (error) {
      console.error('Failed to toggle user status:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || 
      (statusFilter === 'ACTIVE' && user.isActive) ||
      (statusFilter === 'INACTIVE' && !user.isActive);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleIcon = (role: User['role']) => {
    switch (role) {
      case 'admin':
        return <ShieldCheckIcon className="h-5 w-5" />;
      case 'manager':
        return <UserCircleIcon className="h-5 w-5" />;
      case 'staff':
        return <UserGroupIcon className="h-5 w-5" />;
      case 'viewer':
        return <EyeIcon className="h-5 w-5" />;
      default:
        return <UserIcon className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <PageHero
        title="Users"
        subtitle="Manage user accounts and permissions"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add User
          </button>
        }
      />

      {/* Filters */}
      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as User['role'] | 'ALL')}
            className="rounded-md border-0 py-1.5 pl-3 pr-8 text-slate-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
          >
            <option key="ALL-1753461297408" value="ALL">All Roles</option>
            <option key="admin-1753461297408" value="admin">Admin</option>
            <option key="manager-1753461297408" value="manager">Manager</option>
            <option key="staff-1753461297408" value="staff">Staff</option>
            <option key="user-1753461297408" value="user">User</option>
            <option key="viewer-1753461297408" value="viewer">Viewer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
            className="rounded-md border-0 py-1.5 pl-3 pr-8 text-slate-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-500 sm:text-sm sm:leading-6"
          >
            <option key="ALL-1753461297408" value="ALL">All Status</option>
            <option key="ACTIVE-1753461297408" value="ACTIVE">Active</option>
            <option key="INACTIVE-1753461297408" value="INACTIVE">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Last sign-in
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Created
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <UserIcon className="h-6 w-6 text-slate-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-slate-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-slate-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${roleColors[user.role]}`}>
                          {getRoleIcon(user.role)}
                          <span className="ml-1">{roleLabels[user.role]}</span>
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            user.isActive
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-green-200'
                              : 'bg-rose-100 text-rose-800 hover:bg-red-200'
                          } transition-colors`}
                        >
                          {user.isActive ? (
                            <>
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-3 w-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500">
                        {user.lastLoginAt
                          ? new Date(user.lastLoginAt).toLocaleString()
                          : '—'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-slate-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            className="text-cyan-600 hover:text-indigo-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredUsers.length === 0 && (
                <EmptyState
                  className="mt-0"
                  title="No users found"
                  description={
                    searchTerm || roleFilter !== 'ALL' || statusFilter !== 'ALL'
                      ? 'Try adjusting your search or filter criteria.'
                      : 'Get started by creating your first user.'
                  }
                  icon={<UserGroupIcon className="h-6 w-6" />}
                  action={
                    !searchTerm && roleFilter === 'ALL' && statusFilter === 'ALL' ? (
                      <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                        <PlusIcon className="h-4 w-4" />
                        Add User
                      </button>
                    ) : undefined
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      <UserModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSubmit={showCreateModal ? handleCreateUser : handleUpdateUser}
        user={selectedUser}
        isEdit={showEditModal}
      />

      {/* Delete Confirmation Modal */}
      {selectedUser && (
        <BrandModal
          open={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setSelectedUser(null);
          }}
          maxWidthClass="max-w-md"
        >
          <BrandModalHeader
            title="Delete User"
            onClose={() => {
              setShowDeleteConfirm(false);
              setSelectedUser(null);
            }}
          />
          <BrandModalBody>
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <p className="text-sm text-slate-600 text-center">
              Are you sure you want to delete {selectedUser.firstName} {selectedUser.lastName}?
              This action cannot be undone.
            </p>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirm(false);
                setSelectedUser(null);
              }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
            <button type="button" onClick={handleDeleteUser} className="btn btn-danger text-sm">
              Delete
            </button>
          </BrandModalFooter>
        </BrandModal>
      )}
    </div>
  );
}

// User Modal Component
interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateUserData | UpdateUserData) => void;
  user?: User | null;
  isEdit: boolean;
}

function UserModal({ isOpen, onClose, onSubmit, user, isEdit }: UserModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user' as User['role'],
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user && isEdit) {
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        isActive: user.isActive,
      });
    } else {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'user',
        isActive: true,
      });
    }
    setErrors({});
  }, [user, isEdit, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!isEdit && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = isEdit 
      ? { ...formData, password: formData.password || undefined }
      : formData;

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-md">
      <BrandModalHeader title={isEdit ? 'Edit User' : 'Create New User'} onClose={onClose} />
      <BrandModalBody>
          <form id="user-modal-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password {isEdit ? '(leave blank to keep current)' : '*'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as User['role'] }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="user-1753461297408" value="user">User</option>
                <option key="staff-1753461297408" value="staff">Staff</option>
                <option key="manager-1753461297408" value="manager">Manager</option>
                <option key="admin-1753461297408" value="admin">Administrator</option>
                <option key="viewer-1753461297408" value="viewer">Viewer</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-slate-900">
                Active user
              </label>
            </div>
          </form>
      </BrandModalBody>
      <BrandModalFooter>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="user-modal-form"
                className="btn btn-primary text-sm"
              >
                {isEdit ? 'Update User' : 'Create User'}
              </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 